<template>
  <div class="satd-saisies-add">

    <div class="satd-saisies-add__subheader">
      <ui-link-button-meek
        :size="UI.SIZE.SMALL"
        :route="parentRoute"
        bulky
      >
        <ui-icon
          name="navigate_before"
        />
        Saisies
      </ui-link-button-meek>
    </div>

    <div class="satd-saisies-add__card">
      <form-organism
        v-if="saisie"
        :key="saisie.saisie_id"
        v-bind="{
          mode: MODE.EDITION,
          submitCallback: updateCallback,
          cancelCallback: onCancel,
          loadSalarieOptions,
          isPending,
          defaultData: {
            selectedSalarie: saisie.salarie,
            personnes_a_charge: toString(saisie.personnes_a_charge),
            type_saisie: saisie.type_saisie,
            montant: saisieFloatToString(saisie.montant_total),
            date: saisie.date_avis,
            reference_avis: saisie.reference_avis,
            creancier: saisie.creancier,
            date_fin: saisie.date_fin,
            saisie_terminee: saisie.is_termine,
            saisie_historique: saisie.saisie_historique,
          },
        }"
      />
    </div>

  </div>
</template>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables

.satd-saisies-add
  background-color: $background-tertiary
  min-height: 100vh

  &__subheader
    padding: ($layout-spacer-y * .75) $layout-spacer-x 0

  &__card
    padding: space-x(2) $layout-spacer-x
    margin-top: space-x(2)
</style>

<script lang="ts">
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import { getSaisiesRoute } from '~/stores/satd'
import { useToastBuilder } from '~/composables/layout/toast'
import { isEmpty, isNil, lensPath, pipe_, set, tapCatch, toString } from '~/utils/lib/core/prelude'
import { saisieFloatToString } from '~/stores/satd/saisies'
import FormOrganism, { MODE } from '~/components/satd/saisies/form/Organism.vue'
import { useLinkHandler } from '~/composables/core/link-handler'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useStore_satd_saisies_details, type SaisieDetails } from '~/stores/satd/saisies/details'
import { parseDatetime } from '~/utils/lib/core/datetime'
import { NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useStore_satd_saisies_list_salarie } from '~/stores/satd/saisies/list/salarie'

export default defineNuxtComponent({
  components: { FormOrganism },
  setup() {

    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const ToastBuilder = useToastBuilder()

    const navigationContext = useCompanyRequiredNavigationContext()
    const store_satd_salarie = useStore_satd_saisies_list_salarie()
    const store_satd_saisies_details = useStore_satd_saisies_details()

    return {
      ...navigationContext,
      ...useLinkHandler(),
      store_satd_salarie,
      ToastBuilder,
      fetchState,
      fetchWrapper,
      store_satd_saisies_details,
    }
  },
  data() {
    return {
      MODE,
      isPending: false,
      saisie: null as unknown as SaisieDetails,
      saisieFloatToString,
      toString,
    }
  },
  computed: {
    parentRoute() {
      const {
        structure: { id },
        company: { code_societe }
      } = this.fromCompanyLevel()
      return getSaisiesRoute(id, code_societe)
    },
  },
  created() {
    this.fetchWrapper(
      () => this.fetch()
    )
  },
  methods: {
    fetch() {
      const {
        structure: { uuidData },
        company: { societeId }
      } = this.fromCompanyLevel()
      return this
        .store_satd_saisies_details.read(
          {
            uuidData,
            societeId,
            saisie_id: this.$route.params.id as string
        })
        .then((saisie) => {
          const saisie_ = pipe_(
            saisie,
            set(lensPath(['date_avis']), parseDatetime({ inputFormat: undefined, outputFormat: 'YYYY-MM-DD' })(saisie.date_avis)),
            set(
              lensPath(['date_fin']),
              isNil(saisie.date_fin) ?
                '' :
                parseDatetime({ inputFormat: undefined, outputFormat: 'YYYY-MM-DD' })(saisie.date_fin)
            ),
          )
          this.saisie = saisie_
        })
        .catch(tapCatch((err) => {
          this.ToastBuilder
            .error(toString(err))
            .title('Erreur lors de la récupération des informations de la saisie')
            .show()
        }))
    },
    loadSalarieOptions(search: string) {
      const {
        structure: { uuidData },
        company: { societeId }
      } = this.fromCompanyLevel()

      return this.store_satd_salarie.search({
        search,
        uuidData,
        societeId,
      })
    },
    onCancel() {
      const {
        structure: { id: structure_id },
        company: { code_societe }
      } = this.fromCompanyLevel()
      this.goToRoute(getSaisiesRoute(structure_id, code_societe))
    },
    updateCallback(payload) {
      const {
        structure: { id: structure_id, uuidData },
        company: { code_societe, societeId }
      } = this.fromCompanyLevel()
      this.isPending = true

      const data = {
        salarie_id: payload.salarie_id,
        personnes_a_charge: payload.personnes_a_charge,
        type_saisie: payload.type_saisie,
        creancier: payload.creancier,
        date_avis: isEmpty(payload.date_avis) ? undefined : payload.date_avis,
        reference_avis: payload.reference_avis,
        montant_creance: payload.montant_creance,
        date_fin: payload.date_fin,
        is_termine: payload.is_termine,
      }
      return this.store_satd_saisies_details.update({
        uuidData,
        societeId,
        saisie_id: this.saisie.saisie_id,
        data,
      }).then(() => {
        this.ToastBuilder
          .success('Saisie enregistrée avec succès')
          .title('Saisie enregistrée avec succès')
          .show()
        this.goToRoute(getSaisiesRoute(structure_id, code_societe))
      }).catch((err) => {
        this.isPending = false
        this.ToastBuilder
          .error(toString(err))
          .title('Erreur lors de l\'enregistrement de la saisie')
          .show()
      })
    },
  },
})
</script>
